import cx from "classnames";
import { CSSProperties } from "react";

import { useProtospriteInstance } from "../../render/hooks/useProtospriteInstance";
import { useProtospriteScene } from "../../render/hooks/useProtospriteScene";
import { SecondaryRenderCanvas } from "../shared/SecondaryRenderCanvas";
// These could probably be done as CSS classes in the future...
import pngCube from "./Cube.png";
import pngEndTurn from "./EndTurn.png";
import pngGear from "./Gear.png";
import pngHammer from "./Hammer.png";
import "./Icon.css";
import pngPawn from "./Pawn.png";
import pngPieceBishop from "./PieceBishop.png";
import pngPieceKnight from "./PieceKnight.png";
import pngPiecePawn from "./PiecePawn.png";
import pngPieceQueen from "./PieceQueen.png";
import pngPieceRook from "./PieceRook.png";
import pngReplay from "./Replay.png";
import pngUndo from "./Undo.png";
import {
  AllIcons,
  PNGIcons,
  UIIconAnimations,
  UIIconLayers
} from "./iconTypes";
import uiIconPrs from "./ui_icons.prs";

const pngIconMap: Record<PNGIcons, string> = {
  cube: pngCube,
  endTurn: pngEndTurn,
  gear: pngGear,
  hammer: pngHammer,
  pawn: pngPawn,
  pieceBishop: pngPieceBishop,
  pieceKnight: pngPieceKnight,
  piecePawn: pngPiecePawn,
  pieceQueen: pngPieceQueen,
  pieceRook: pngPieceRook,
  // TODO: structure and category icons use the pawn until art exists
  structureStore: pngPiecePawn,
  structureTurret: pngPiecePawn,
  structureAltar: pngPiecePawn,
  structureRelic: pngPiecePawn,
  structureBanner: pngPiecePawn,
  structureKeystone: pngPiecePawn,
  structureWell: pngPiecePawn,
  structureResearch: pngPiecePawn,
  categoryIndustry: pngPiecePawn,
  categoryDefense: pngPiecePawn,
  categoryArtifacts: pngPiecePawn,
  zoomIn: pngPiecePawn,
  zoomOut: pngPiecePawn,
  replay: pngReplay,
  undo: pngUndo
};

function isPngIcon(iconName: AllIcons): iconName is PNGIcons {
  return iconName in pngIconMap;
}

function isProtospriteIcon(iconName: AllIcons): iconName is UIIconAnimations {
  return !isPngIcon(iconName);
}

export type PNGIconProps = {
  icon: PNGIcons;
  className?: string;
  style?: CSSProperties;
  mode?: "mask" | "image";
};

export function PNGIcon(props: PNGIconProps) {
  const { icon, className, style, mode } = props;
  const imageSrc = pngIconMap[icon];
  return (
    <span
      className={cx("png-icon", mode !== "image" && "png-mask-icon", className)}
      style={{
        ...style,
        backgroundImage: mode === "image" ? `url("${imageSrc}")` : undefined,
        maskImage: mode !== "image" ? `url("${imageSrc}")` : undefined
      }}
    />
  );
}

export type ProtospriteIconProps = {
  icon: UIIconAnimations;
  className?: string;
  style?: CSSProperties;
};

export function ProtospriteIcon(props: ProtospriteIconProps) {
  const { icon, className, style } = props;
  const sprite = useProtospriteInstance<UIIconLayers, UIIconAnimations>(
    uiIconPrs
  );
  const { scene, camera } = useProtospriteScene(sprite, icon);
  return (
    <SecondaryRenderCanvas
      scene={scene}
      camera={camera}
      className={className}
      style={style}
    />
  );
}

export type IconProps = {
  icon: AllIcons;
  className?: string;
  style?: CSSProperties;
};

export function Icon(props: IconProps) {
  const { icon, className, style } = props;
  if (isPngIcon(icon)) {
    return <PNGIcon icon={icon} className={className} style={style} />;
  }
  if (isProtospriteIcon(icon)) {
    return <ProtospriteIcon icon={icon} className={className} style={style} />;
  }
  return null;
}
